<template>
    <div class="vue-map-container" :class="{ 'with-message': messagePanelAvailable, 'vue-map-osm-container': useOSM }">
        <div class="vue-map--wrapper">
            <!-- Use OSM/Leaflet map if modulo_geocoding is not 'google' -->
            <MapsOSM
                v-if="useOSM"
                :points="points"
                :notCenteredPoints="notCenteredPoints"
                :center="center"
                :zoom="zoom"
                :allowToSelectPosition="allowToSelectPosition"
                :selectPositionIcon="selectPositionIcon"
                :drawRouteData="drawRouteData"
                @zoom_changed="zoomChange"
                @center_changed="centerChange"
                @drag_start="dragStart"
                @drag_end="dragEnd"
                @map_click="onClick"
            />
            <!-- Use Google Maps if modulo_geocoding is 'google' or not set -->
            <template v-else>
                <img class="selected-position" :src="selectPositionIcon" v-if="allowToSelectPosition && locationEnabled" />
                <gmap-map
                    :center="centerOfTheMap"
                    :zoom="zoom"
                    style="height: 100%; width: 100%"
                    ref="map"
                    :options="options"
                    v-on:zoom_changed="zoomChange"
                    v-on:center_changed="centerChange"
                    v-on:dragstart="dragStart"
                    v-on:dragend="dragEnd"
                    v-on:click="onClick"
                >
                    <gmap-marker
                        v-for="(m, index) in pointsOnMap"
                        v-if="m.location"

                        :icon="m.icon"
                        :visible="!m.invisible"
                        :key="index"
                        :position="m.location"
                        :clickable="true"

                        @click="m.click ? m.click() : null"
                    ></gmap-marker>
                </gmap-map>
            </template>
        </div>
    </div>
</template>
<script>
import { mapGetters } from 'vuex';
import { mapStyles } from 'resources/default/maps';
import bus from '@/services/bus-event';
import { debounce } from '@/services/utility';
import { TRIP_STATE } from '@/store/mutation-types';
import MapsOSM from './MapsOSM.vue';

export default {
    name: 'vue-maps',
    props: {
        points: {
            type: Array,
            required: false,
            default: () => {
                return [];
            }
        },
        notCenteredPoints: {
            type: Array,
            required: false,
            default: () => {
                return [];
            }
        },
        center: {
            required: false,
            default: () => {
                return {
                    lat: 32,
                    lng: 64
                };
            }
        },
        zoom: {
            type: Number,
            required: false,
            // [TODO] Handler defualt zoom
            default: 15
        },
        allowToSelectPosition: {
            type: Boolean,
            required: false,
            default: false
        },
        selectPositionIcon: {
            required: false,
            default: null
        },
        drawRouteData: {
            required: false,
            default: null
        }
    },
    data () {
        return {
            routeDrawed: false,
            routePolylines: [],
            options: {
                styles: mapStyles,
                disableDefaultUI: true
            },
            debounceFuncListener: {
                'idle': null,
                'drag:': null
            },
            mapIsMoving: false,
            mapReCenterTimer: null,
            mapMoved: false,
            moveTimer: null,
            isProgrammaticMove: false
        };
    },
    components: {
        MapsOSM
    },
    computed: {
        ...mapGetters({
            'isChofer': 'auth/isChofer',
            'locationEnabled': 'geolocation/locationEnabled',
            'settings': 'company/config',
            'currentTrip': 'auth/currentTrip'
        }),
        useOSM () {
            // Use OSM if modulo_geocoding is set and not 'google', default to google
            return this.settings && this.settings.modulo_geocoding && this.settings.modulo_geocoding !== 'google';
        },
        pointsOnMap () {
            let pointOfMap = this.allowToSelectPosition ? [] : this.points;
            if (this.notCenteredPoints && this.notCenteredPoints.length) {
                pointOfMap = [...pointOfMap, ...this.notCenteredPoints]; // Spread operator for concat
            }
            return pointOfMap;
        },
        centerOfTheMap () {
            if (this.points && this.points.length && this.points[0].location !== null) {
                // devuelvo el primero, el fitBound se va a centrar despues de este evento,
                // la prop center es obligatoria
                return this.points[0].location;
            } else {
                if (this.center && this.center.lat) {
                    return this.center;
                } else {
                    return {
                        lat: 32,
                        lng: 64
                    };
                }
            }
        },
        messagePanelAvailable () {
            console.log('messagePanelAvailable', this.settings && this.settings.modulo_mensajes && this.currentTrip && this.currentTrip.frontState !== TRIP_STATE.PENDING);
            return this.settings && this.settings.modulo_mensajes && this.currentTrip && this.currentTrip.frontState !== TRIP_STATE.PENDING;
        }
    },
    mounted () {
        this.language = this.$getLang();
        bus.on('set_center', this.setCenter);
        bus.on('cordova_resume', this.cordova_resume);
        if (this.$refs.map) {
            this.renderMap();
        }
    },
    methods: {
        emitLocation () {
            // Don't emit location if the map move was programmatic (not user-initiated)
            if (this.isProgrammaticMove) {
                return;
            }
            if (this.$refs.map) { // when you change view, emit location event it's fire, maybe with have to make a better check of this
                let newPoint = {
                    location: this.$refs.map.$mapObject.getCenter(),
                    icon: this.selectPositionIcon
                };
                if (newPoint.location) {
                    let emitPoint = {
                        location: {
                            lat: newPoint.location.lat(),
                            lng: newPoint.location.lng()
                        }
                    };
                    bus.emit('map_point_selected', emitPoint);
                }
            } else {
                console.log('Emit Location fired in another view');
            }
        },
        handleMoveMap () {
            this.mapIsMoving = true;
            if (this.mapReCenterTimer) {
                window.clearTimeout(this.mapReCenterTimer);
            }
            this.mapReCenterTimer = window.setTimeout(() => {
                this.mapIsMoving = false;
            }, 50 * 1000);
        },
        zoomChange (newZoom) {
            this.handleMoveMap();
            this.mapMoved = true;
            if (this.moveTimer) {
                clearTimeout(this.moveTimer);
            }
            this.moveTimer = setTimeout(() => {
                this.mapMoved = false;
            }, 60 * 1000);
            // disabled freeze map when zoom
            this.$emit('zoom_changed', newZoom);
        },
        setCenter (location) {
            console.log('map setCenter', location);
            if (this.$refs.map && location && location.lat !== undefined && location.lng !== undefined) {
                // Reset mapIsMoving flag to allow forced center update
                // This ensures customPosition updates work even if map was recently moved
                this.mapIsMoving = false;
                if (this.mapReCenterTimer) {
                    window.clearTimeout(this.mapReCenterTimer);
                    this.mapReCenterTimer = null;
                }
                // Set flag to prevent emitLocation from firing during programmatic move
                this.isProgrammaticMove = true;
                // Force update the map center without animation for immediate update
                this.$refs.map.$mapObject.setZoom(15);
                this.$refs.map.$mapObject.panTo(location);
                // Reset flag after a longer delay to ensure idle event is processed
                // Google Maps' idle event can fire with some delay, so we need more time
                setTimeout(() => {
                    this.isProgrammaticMove = false;
                }, 500);
            }
        },
        dragStart (event) {
            this.handleMoveMap();
            this.$emit('drag_start', event);
        },
        dragEnd (event) {
            this.mapMoved = true;
            if (this.moveTimer) {
                clearTimeout(this.moveTimer);
            }
            this.moveTimer = setTimeout(() => {
                this.mapMoved = false;
            }, 60 * 1000);
            this.$emit('drag_end', event);
        },
        onClick (event) {
            this.$emit('map_click', event);
        },
        centerChange (newCenter) {
            if (newCenter !== null) {
                // Handle both Google Maps LatLng object (has lat()/lng() methods)
                // and plain objects from OSM (has lat/lng properties)
                const lat = typeof newCenter.lat === 'function' ? newCenter.lat() : newCenter.lat;
                const lng = typeof newCenter.lng === 'function' ? newCenter.lng() : newCenter.lng;
                this.$emit('center_changed', { lat, lng });
            }
        },
        doFitBounds (points) {
            console.log('doFitBounds', points, this.mapMoved);
            if (this.mapMoved) {
                return;
            }
            if (points && points.length > 1) {
                console.log('this.$refs.map.$mapObject', this.$refs.map.$mapObject);
                if (this.$refs.map.$mapObject) {
                    /* eslint-disable no-undef */
                    let bounds = new google.maps.LatLngBounds();
                    for (let i = 0; i < points.length; i++) {
                        bounds.extend(points[i].location);
                    }
                    let pointNorthEast = {
                        lat: 0,
                        lng: 0
                    };
                    let pointSouthWest = {
                        lat: 0,
                        lng: 0
                    };
                    let modifier = {
                        north: 0.001,
                        est: 0.0005,
                        south: 0.001,
                        west: 0.0005
                    };
                    if (this.isChofer) {
                        modifier.south = 0.085;
                    } else {
                        modifier.north = 0.035;
                    }
                    console.log('modifier', modifier);
                    pointNorthEast.lat = bounds.getNorthEast().lat() + modifier.north;
                    pointNorthEast.lng = bounds.getNorthEast().lng() + modifier.est;
                    pointSouthWest.lat = bounds.getSouthWest().lat() - modifier.south;
                    pointSouthWest.lng = bounds.getSouthWest().lng() - modifier.west;
                    /* eslint-disable no-undef */
                    let extendPointNorthEast = new google.maps.LatLng(pointNorthEast.lat, pointNorthEast.lng);
                    let extendPointSouthWest = new google.maps.LatLng(pointSouthWest.lat, pointSouthWest.lng);
                    bounds.extend(extendPointNorthEast);
                    bounds.extend(extendPointSouthWest);
                    this.$refs.map.$mapObject.fitBounds(bounds);
                }
            } else if (points && points.length === 1) {
                this.setCenter(points[0].location);
            }
        },
        drawRoute () {
            this.computeAndDrawRoute();
        },
        async getRoutesLibrary () {
            /* eslint-disable no-undef */
            if (typeof google === 'undefined' || !google.maps) {
                throw new Error('Google Maps not available');
            }

            if (typeof google.maps.importLibrary === 'function') {
                // @ts-ignore
                return google.maps.importLibrary('routes');
            }

            return { Route: google.maps.routes && google.maps.routes.Route ? google.maps.routes.Route : null };
        },
        clearRoute () {
            if (this.routePolylines && this.routePolylines.length) {
                this.routePolylines.forEach(p => p.setMap(null));
            }
            this.routePolylines = [];
            this.routeDrawed = false;
        },
        async computeAndDrawRoute () {
            if (!this.$refs.map || !this.$refs.map.$mapObject || !this.drawRouteData) return;
            this.clearRoute();

            try {
                /* eslint-disable no-undef */
                const lib = await this.getRoutesLibrary();
                const Route = lib && lib.Route ? lib.Route : (google.maps.routes && google.maps.routes.Route ? google.maps.routes.Route : null);

                // Fallback to legacy only if routes library isn't available (older script loader).
                if (!Route || typeof Route.computeRoutes !== 'function') {
                    // Create legacy renderer ONLY if needed (deprecated 2026-02-25).
                    if (!this.directionsService) {
                        this.directionsService = new google.maps.DirectionsService();
                    }
                    if (!this.directionsDisplay) {
                        let maincolor = getComputedStyle(document.documentElement).getPropertyValue('--main-color');
                        let polyOptions = new google.maps.Polyline({
                            strokeColor: maincolor,
                            strokeOpacity: 0.8,
                            strokeWeight: 4
                        });
                        let options = {
                            preserveViewport: true,
                            suppressInfoWindows: true,
                            suppressMarkers: true,
                            polylineOptions: polyOptions
                        };
                        this.directionsDisplay = new google.maps.DirectionsRenderer(options);
                        this.directionsDisplay.setMap(this.$refs.map.$mapObject);
                    }
                    if (this.directionsService && this.directionsDisplay) {
                        let request = {
                            origin: this.drawRouteData.origin,
                            destination: this.drawRouteData.destination,
                            travelMode: 'DRIVING'
                        };
                        this.directionsService.route(request, (response, status) => {
                            if (status === 'OK') {
                                this.directionsDisplay.setDirections(response);
                                this.routeDrawed = true;
                            } else {
                                console.error('Directions request failed, status=' + status);
                            }
                        });
                    }
                    return;
                }

                const request = {
                    origin: this.drawRouteData.origin,
                    destination: this.drawRouteData.destination,
                    travelMode: 'DRIVING',
                    fields: ['path']
                };

                const { routes } = await Route.computeRoutes(request);
                if (!routes || !routes.length || !routes[0].path || !routes[0].path.length) {
                    return;
                }

                let maincolor = getComputedStyle(document.documentElement).getPropertyValue('--main-color');
                const polyline = new google.maps.Polyline({
                    path: routes[0].path,
                    strokeColor: maincolor,
                    strokeOpacity: 0.8,
                    strokeWeight: 4
                });
                polyline.setMap(this.$refs.map.$mapObject);
                this.routePolylines = [polyline];
                this.routeDrawed = true;
            } catch (e) {
                console.error('computeRoutes failed', e);
            }
        },
        renderMap () {
            if (this.$refs.map) {
                // Promise que indica cuando el mapa esta creado.
                this.$refs.map.$mapCreated.then(() => {
                    /* eslint-disable no-undef */
                    this.doFitBounds(this.points);

                    if (this.allowToSelectPosition) {
                        let debounceFunc = debounce(this.emitLocation, 2000, false);
                        this.debounceFuncListener.idle = google.maps.event.addListener(this.$refs.map.$mapObject, 'idle', debounceFunc);
                        this.debounceFuncListener.drag = google.maps.event.addListener(this.$refs.map.$mapObject, 'drag', debounceFunc);
                    }

                    /* eslint-disable no-undef */
                    // Don't instantiate DirectionsRenderer up-front (deprecated 2026-02-25).
                    // We'll create it only if we must fall back to legacy routing.
                    this.directionsService = null;
                    this.directionsDisplay = null;
                    if (this.settings.modulo_sugerir_ruta_origen_destino && this.drawRouteData) {
                        this.drawRoute();
                    }
                });
            }
        },
        cordova_resume () {
            if (window.device && window.device.platform === 'ios') {
                // Fix para manchas negras en iOS en el mapa cuando vengo desde background
                this.refreshMap();
            }
        },
        refreshMap () {
            let map = this.$refs.map.$mapObject;
            let actualZoom = map.getZoom();
            map.setZoom(actualZoom - 1);
            setTimeout(function () {
                console.log('setZoom at refreshMap');
                map.setZoom(actualZoom);
            }, 300);
        }
    },
    watch: {
        points (newValue, oldValue) {
            if (newValue && newValue.length > 0) {
                setTimeout(() => {
                    console.log('change in points', this.mapIsMoving);
                    if (!this.mapIsMoving) {
                        this.doFitBounds(this.points);
                    } else {
                    }
                }, 200);
            }
        },
        drawRouteData () {
            if (this.settings.modulo_sugerir_ruta_origen_destino) {
                if (this.drawRouteData) {
                    this.drawRoute();
                } else if (this.routeDrawed) {
                    this.clearRoute();
                    if (this.directionsDisplay) {
                        this.directionsDisplay.set('directions');
                    }
                }
            }
        }

    },
    beforeDestroy () {
        bus.off('set_center', this.setCenter);
        bus.off('cordova_resume', this.refreshMap);
        if (this.debounceFuncListener.idle !== null) {
            google.maps.event.removeListener(this.debounceFuncListener.idle);
            google.maps.event.removeListener(this.debounceFuncListener.drag);
        }
    }
};
</script>

<style scoped>
    .vue-map-container {
        height: calc(100vh - var(--header-height) - var(--ios-pad));
        overflow: hidden;
    }
    .vue-map--wrapper {
        width: 100%;
        height: 100%;
    }
    img.selected-position {
        top: 50%;
        left: 50%;
        margin-top: -38px;
        margin-left: -10px;
        position: absolute;
        z-index: 1;
        pointer-events: none;
    }
    @media screen and (max-height: 700px) {

        .vue-map--wrapper{
            height: 150%;
            overflow: hidden;
        }
        img.selected-position {
            top: 75%;
        }
    }
</style>



// WEBPACK FOOTER //
// src/components/elements/Maps.vue